import type {
  CapacityLabel,
  CapacityStatus,
  QuantityUnit,
  BankCapacityRow,
} from './capacity.types'
import { formatCapacity } from '@/helper/valueChange.helper'

export const PILOT_BANK = 'Union Investment Privatfonds GmbH'

export const CAPACITY_UNITS: QuantityUnit[] = ['TOJ', 'Std.', 'Tr.', 'Std.']
export const CAPACITY_UNIT_BY_LABEL: Record<CapacityLabel, QuantityUnit> = {
  TOJ: 'TOJ',
  Beratung: 'Std.',
  Schulung: 'Tr.',
  Pilot: 'Std.',
}

/**
 * Reads a German display value (`1.234,5`, `85 %`). The dot is a thousands separator here, so
 * never pass a raw number such as `String(3.5)`: it would read as 35.
 */
export function parseNumber(value: string): number {
  const cleaned = value.replace(/[^\d,-]/g, '').replace(',', '.')
  const parsed = Number(cleaned)
  return Number.isFinite(parsed) ? parsed : 0
}

export function formatQuantity(value: string, unit: QuantityUnit): string {
  return value === '—' ? value : `${value} ${unit}`
}

export function formatQuantityPair(done: string, ordered: string, unit: QuantityUnit): string {
  return `${done}/${ordered} ${unit}`
}

export function capacityUsagePct(ordered: string, frame: string): string {
  const base = parseNumber(frame)
  if (!base) return '—'
  return `${Math.round((parseNumber(ordered) / base) * 100)} %`
}

export function capacityRestPct(ordered: string, frame: string): string {
  const base = parseNumber(frame)
  if (!base) return '—'
  return `${Math.round(((base - parseNumber(ordered)) / base) * 100)} %`
}

export function serviceLabel(index: number): CapacityLabel {
  return (['TOJ', 'Beratung', 'Schulung', 'Pilot'] as CapacityLabel[])[index]!
}

export function zeitzielStatus(value: string): {
  label: string
  tone: 'green' | 'yellow' | 'red'
} {
  const index = parseNumber(value) / 100
  if (index > 1.1) return { label: 'deutlich über Plan', tone: 'yellow' }
  if (index > 1.05) return { label: 'leicht über Plan', tone: 'yellow' }
  if (index < 0.9) return { label: 'deutlich unter Plan', tone: 'red' }
  if (index < 0.95) return { label: 'leicht unter Plan', tone: 'red' }
  return { label: 'im Zielkorridor', tone: 'green' }
}

export function formatZeitzielIndex(value: string): string {
  const pct = parseNumber(value)
  if (!pct) return '—'
  return (pct / 100).toFixed(2).replace('.', ',')
}

export function statusColor(status: CapacityStatus): string {
  if (status === 'green') return 'green.solid'
  if (status === 'yellow') return 'yellow.solid'
  return 'red.solid'
}

export function statusLabel(status: CapacityStatus): string {
  if (status === 'green') return 'Im Plan'
  if (status === 'yellow') return 'Prüfen'
  return 'Auffällig'
}

export function bankListStatus(row: BankCapacityRow): CapacityStatus {
  const ordered = row.ordered.map(parseNumber)
  const done = row.done
  const hasActiveOrder = ordered.some(v => v > 0)
  const hasOpenOrder = ordered.some((v, i) => v > done[i]!)
  if (!hasActiveOrder) return 'red'
  if (hasOpenOrder) return 'yellow'
  return 'green'
}

export function lastName(name: string): string {
  return name.trim().split(/\s+/).at(-1)?.toLowerCase() ?? name.toLowerCase()
}

const percentFormat = new Intl.NumberFormat('de-DE', { maximumFractionDigits: 1 })

/** Tooltip value of a unit bar: `3,5 TOJ`, so fractional units stay readable (SIB-1133). */
export function formatUnitTooltipValue(value: number, unit: string): string {
  return `${formatCapacity(value)} ${unit}`.trim()
}

/** Tooltip value of the progress line: `12,5%`, or `—` for a month still to come. */
export function formatProgressTooltipValue(value: number | null | undefined): string {
  return value != null ? `${percentFormat.format(value)}%` : '—'
}

export type MonthlyEntry = {
  month: string
  Plan: number
  Durchgeführt: number | null
}

export type MonthlyProgressItem = {
  month: number
  tojDone: number
  consultingDone: number
  trainingDone: number
  pilotDone: number
}

/** Share of the ordered units in percent, to one decimal so 0.5 units do not read as 0 %. */
function progressPct(done: number, ordered: number): number {
  return Math.round((done / ordered) * 1000) / 10
}

export function buildMonthlyProgressData(
  ordered: string[],
  done: number[],
  monthlyData?: MonthlyProgressItem[]
): MonthlyEntry[] {
  const totalOrdered = ordered.reduce((acc, v) => acc + parseNumber(v), 0)
  const months = [
    'Jan',
    'Feb',
    'Mär',
    'Apr',
    'Mai',
    'Jun',
    'Jul',
    'Aug',
    'Sep',
    'Okt',
    'Nov',
    'Dez',
  ]
  const currentMonth = new Date().getMonth()

  if (monthlyData && monthlyData.length > 0 && totalOrdered > 0) {
    // The backend already sends running totals per month; a month without an entry keeps the
    // total of the month before.
    let cumDone = 0
    const cumByMonth: Record<number, number> = {}
    for (let m = 1; m <= currentMonth + 1; m++) {
      const entry = monthlyData.find(d => d.month === m)
      if (entry)
        cumDone = entry.tojDone + entry.consultingDone + entry.trainingDone + entry.pilotDone
      cumByMonth[m] = cumDone
    }
    return months.map((month, i) => ({
      month,
      Plan: Math.round(((i + 1) / 12) * 100),
      Durchgeführt: i <= currentMonth ? progressPct(cumByMonth[i + 1] ?? 0, totalOrdered) : null,
    }))
  }

  // fallback: estimate from totals (no real monthly breakdown)
  const totalDone = done.reduce((acc, v) => acc + v, 0)
  const doneRatio = totalOrdered > 0 ? Math.min(totalDone / totalOrdered, 1) : 0
  return months.map((month, i) => ({
    month,
    Plan: Math.round(((i + 1) / 12) * 100),
    Durchgeführt: i <= currentMonth ? progressPct(doneRatio * (i + 1), 12) : null,
  }))
}
